// components/modules/Pricing/PricingCard.tsx
import { GreenTick } from "@/components/common/Icons/Pricing/GreenTick";
import { Translate } from "@/components/common/Translate/Translate";
import React, { useEffect, useState } from "react";
import useSubscription from "@/modules/Dashboard/Pricing/useSubscription";
import { InfinityCircularLoadingIcon } from "components/common/Icons/InfinityCircularLoadingIcon";
import { cn } from "@/lib/utils/cn";
import useAuth from "components/modules/Auth/useAuth";
import { ToolTip } from "components/common/ToolTip/ToolTip";
import { translateFunc } from "@/utils/translateFunc";

export interface IPricingFeature {
    feature: React.ReactNode;
}

export interface IPricingPlan {
    id?: string;
    title: React.ReactNode;
    subtitle: React.ReactNode;
    price: number;
    period: React.ReactNode;
    features: IPricingFeature[];
    isPopular?: boolean;
    stripeProductId?: string;
}

export interface PricingPlanProps {
    plan: IPricingPlan;
}

export const PricingCard: React.FC<PricingPlanProps> = ({ plan }: PricingPlanProps) => {
    const { title, subtitle, price, period, features, isPopular } = plan;

    return (
        <div
            id={`pricing-cards-section-${plan.id}`}
            className={`relative p-6 sm:p-8 hover:outline hover:outline-4 hover:outline-blue-500 ${
                isPopular ? "outline outline-2 outline-blue-500" : "border border-gray-300"
            }`}
        >
            {isPopular && (
                <div className="absolute -top-4 left-0 right-0 text-center">
                    <span className="bg-blue-500 text-white px-4 sm:px-6 py-1 rounded-full text-xs sm:text-sm">
                        <Translate keyName="most-popular" />
                    </span>
                </div>
            )}

            <div className="flex flex-col items-center space-y-2 sm:space-y-4 mb-6 sm:mb-8">
                <div className="flex items-center space-x-2">
                    <span className="font-bold text-lg sm:text-xl">{title}</span>
                </div>
                <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-300">{subtitle}</div>
                <div className="mt-2 sm:mt-4 text-2xl sm:text-3xl font-bold">
                    ${price}
                    <span className="text-xs sm:text-sm font-normal text-gray-600 dark:text-gray-100">/{period}</span>
                </div>
            </div>

            <PricingGetStartedButton stripeProductId={plan.stripeProductId} />
            <div className="space-y-4 sm:space-y-6">
                <div className="font-medium text-sm sm:text-base">
                    {title} <Translate keyName="includes" />
                </div>
                <div className="space-y-2 sm:space-y-3">
                    {features.map((feature, index) => (
                        <div key={index} className="flex items-center text-xs sm:text-sm">
                            <GreenTick />
                            <span className="text-gray-600 dark:text-gray-200">{feature.feature}</span>
                        </div>
                    ))}
                </div>
                <div className="">
                    <div className="absolute bottom-3 left-0 right-0 text-center">
                        <a href="#features-section" className="text-blue-500 text-xs sm:text-sm hover:underline">
                            <Translate keyName="see-all-features" />
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const PricingGetStartedButton = ({ stripeProductId }: { stripeProductId?: string }) => {
    const { handleSubscribeAction, isBusy, activeSubscription } = useSubscription();
    const { isLoggedIn } = useAuth();

    /* To fix hydration error need use state here */
    const [isProductActive, setIsProductActive] = useState<boolean>(false);
    const [buttonText, setButtonText] = useState<string>("get-started");
    useEffect(() => {
        if (isLoggedIn() && activeSubscription?.result) {
            setIsProductActive(
                activeSubscription?.result?.stripeProductId === stripeProductId &&
                    activeSubscription?.result?.irSubscriptionStatus === "active",
            );
        }

        if (isProductActive) {
            setButtonText("subscribed");
        } else {
            setButtonText("get-started");
        }
    }, [isLoggedIn]);

    return (
        <ToolTip
            inactive={!isProductActive}
            text={translateFunc("already-subscribed")}
            containerClassNames="w-full mb-6 sm:mb-8 "
        >
            <button
                disabled={isProductActive}
                onClick={async () => {
                    await handleSubscribeAction(stripeProductId);
                }}
                className={cn(
                    "w-full bg-blue-500 text-white py-2 sm:py-3 rounded-md hover:bg-blue-600 transition-colors  disabled:cursor-not-allowed",
                    isProductActive && "bg-green-600 hover:bg-green-700",
                )}
            >
                {!isBusy ? (
                    <div className="w-full">
                        <Translate keyName={buttonText} />
                    </div>
                ) : (
                    <div className="flex justify-center">
                        <InfinityCircularLoadingIcon className={`h-5 w-5 ml-2  stroke-cyan-50  animate-spin`} />
                    </div>
                )}
            </button>
        </ToolTip>
    );
};
